/**
 * Runs schema.ts's real @livestore/livestore query-builder objects (tables.X.select()/.insert()/
 * .update()/.delete(), all real -- only the storage engine underneath was ever broken) against a
 * real SQLite connection via the driver interface below. Keeps schema.ts as the single source of
 * truth for the event/table shape; this module only executes the SQL it already generates.
 */
import { rawMaterializers } from '@/livestore/schema.js';

export interface SqlQueryLike {
  asSql(): { bindValues?: readonly unknown[]; query: string };
}

export interface SqlDriver {
  execute(sql: string, values: readonly unknown[]): Promise<void>;
  query<T = Record<string, unknown>>(sql: string, values: readonly unknown[]): Promise<T[]>;
}

export interface LiveStoreEvent {
  args: Record<string, unknown>;
  name: string;
}

async function runMutation(driver: SqlDriver, q: SqlQueryLike): Promise<void> {
  const { bindValues = [], query: sql } = q.asSql();
  await driver.execute(sql, bindValues);
}

export function createEngine(driver: SqlDriver) {
  async function commit(event: LiveStoreEvent): Promise<void> {
    const materialize = (
      rawMaterializers as unknown as Record<
        string,
        (args: Record<string, unknown>) => SqlQueryLike | SqlQueryLike[]
      >
    )[event.name];
    if (!materialize) {
      throw new Error(`engine.ts: no materializer registered for event "${event.name}"`);
    }
    const result = materialize(event.args);
    const queries = Array.isArray(result) ? result : [result];
    for (const q of queries) await runMutation(driver, q);
    await driver.execute('INSERT INTO "events" ("id", "name", "args", "ts") VALUES (?, ?, ?, ?)', [
      crypto.randomUUID(),
      event.name,
      JSON.stringify(event.args),
      Date.now(),
    ]);
  }

  async function query<T = Record<string, unknown>>(q: SqlQueryLike): Promise<T[]> {
    const { bindValues = [], query: sql } = q.asSql();
    return driver.query<T>(sql, bindValues);
  }

  return { commit, query };
}

export type Engine = ReturnType<typeof createEngine>;
